import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { validateMyStatsForLeaderboard } from '../utils/dataUtils';
import PageLayout from '../components/PageLayout';

const CheckMyStats = () => {
  const { t } = useLanguage();
  const location = useLocation();

  // Utility function to format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const now = new Date();
    // Parse the UTC timestamp correctly by appending 'Z' to indicate UTC
    const time = new Date(timestamp + 'Z');
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    // Handle negative differences (future dates)
    if (diffInSeconds < 0) {
      return 'just now';
    }
    
    if (diffInSeconds < 60) {
      return `${diffInSeconds} secs ago`;
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hr${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(diffInSeconds / 31536000);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  };
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [addingToLeaderboard, setAddingToLeaderboard] = useState(false);
  const [addedToLeaderboard, setAddedToLeaderboard] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [sortColumn, setSortColumn] = useState('composite_score');
  const [sortDirection, setSortDirection] = useState('desc');
  const fileInputRef = useRef(null);
  const isDevEnv = false;
  const websiteURL = isDevEnv ? 'http://localhost' : '';

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      // Track file upload initiation via drag & drop
      if (typeof gtag !== 'undefined') {
        gtag('event', 'file_upload_initiated', {
          'page': 'stats',
          'upload_method': 'drag_drop'
        });
      }
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid image format. Please upload JPEG, PNG, WEBP, or GIF files.');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size exceeds 10MB limit.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(`${websiteURL}/image-upload-stats.php`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setPlayerStats(result);
        // Track successful file upload
        if (typeof gtag !== 'undefined') {
          gtag('event', 'file_upload_completed', {
            'page': 'stats',
            'file_type': file.type,
            'file_size': file.size
          });
        }
        // Automatically add to leaderboard
        await addToLeaderboard(result);
      } else {
        setError(result.error || 'Failed to process image');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetStats = () => {
    setPlayerStats(null);
    setError(null);
    setAddedToLeaderboard(false);
    setIsUpdate(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const loadLeaderboard = async () => {
    setLoadingLeaderboard(true);
    setLeaderboardError(null);
    
    try {
      const response = await fetch(`${websiteURL}/get-leaderboard.php`);
      const data = await response.json();
      
      if (response.ok) {
        setLeaderboard(data);
      } else {
        setLeaderboardError(data.error || 'Failed to load leaderboard');
      }
    } catch (err) {
      setLeaderboardError('Network error: ' + err.message);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const addToLeaderboard = async (stats = null) => {
    const statsToAdd = stats || playerStats;
    if (!statsToAdd) return;
    
    // Check minimum battle requirement
    const totalBattles = statsToAdd.combat_performance_stats?.total_battles || 0;
    if (totalBattles < 2000) {
      setError(t('stats.insufficientBattlesMessage', { battles: totalBattles.toLocaleString() }));
      return;
    }

    // Validate win % and kills per battle before sending to leaderboard
    const validation = validateMyStatsForLeaderboard(statsToAdd);
    if (!validation.isValid) {
      setError(t(validation.errorKey));

      // Track validation failure in Google Analytics (optional, mirrors existing patterns)
      if (typeof gtag !== 'undefined') {
        gtag('event', 'error_occurred', {
          error_type: 'validation',
          error_message: validation.errorKey,
          calculator_type: 'my_stats',
        });
      }
      return;
    }
    
    setAddingToLeaderboard(true);
    
    try {
      const response = await fetch(`${websiteURL}/add-to-leaderboard.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(statsToAdd),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setAddedToLeaderboard(true);
        setIsUpdate(result.isUpdate || false);
        // Reload leaderboard to show updated data
        loadLeaderboard();
      } else {
        setError(result.error || 'Failed to add to leaderboard');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setAddingToLeaderboard(false);
    }
  };

  // Filter and sort leaderboard data
  const getFilteredPlayers = () => {
    if (!leaderboard || !leaderboard.players) return [];
    
    // First filter by search term
    let filteredPlayers = leaderboard.players.filter(player => 
      player.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.player_id.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Then sort by the selected column and direction
    filteredPlayers.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'rank':
          aValue = a.rank || 0;
          bValue = b.rank || 0;
          break;
        case 'username':
          aValue = (a.username || '').toLowerCase();
          bValue = (b.username || '').toLowerCase();
          break;
        case 'player_id':
          aValue = (a.player_id || '').toLowerCase();
          bValue = (b.player_id || '').toLowerCase();
          break;
        case 'composite_score':
          aValue = a.composite_score || 0;
          bValue = b.composite_score || 0;
          break;
        case 'win_percentage':
          aValue = a.win_percentage || 0;
          bValue = b.win_percentage || 0;
          break;
        case 'total_wins':
          aValue = a.total_wins || 0;
          bValue = b.total_wins || 0;
          break;
        case 'total_battles':
          aValue = a.total_battles || 0;
          bValue = b.total_battles || 0;
          break;
        case 'kills_per_battle':
          aValue = a.kills_per_battle || 0;
          bValue = b.kills_per_battle || 0;
          break;
        case 'inventory_power':
          aValue = a.inventory_power || 0;
          bValue = b.inventory_power || 0;
          break;
        case 'updated_at':
          aValue = new Date(a.updated_at || 0).getTime();
          bValue = new Date(b.updated_at || 0).getTime();
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric comparison
      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return filteredPlayers;
  };

  const getPaginatedPlayers = () => {
    const filteredPlayers = getFilteredPlayers();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPlayers.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredPlayers = getFilteredPlayers();
    return Math.ceil(filteredPlayers.length / itemsPerPage);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set it as active and default to desc
      setSortColumn(column);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Calculate gaming time based on total battles
  const calculateGamingTime = (totalBattles) => {
    if (!totalBattles || totalBattles === 0) return null;
    
    const totalMinutes = totalBattles * 4; // 4 minutes per battle
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    let timeString = '';
    if (days > 0) {
      timeString += `${days}d `;
    }
    if (remainingHours > 0) {
      timeString += `${remainingHours}h `;
    }
    if (minutes > 0) {
      timeString += `${minutes}m`;
    }
    
    return {
      formatted: timeString.trim(),
      totalMinutes,
      totalBattles
    };
  };

  // Load leaderboard on component mount
  useEffect(() => {
    loadLeaderboard();
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Set document title
  useEffect(() => {
    document.title = `${t('stats.title')} - ${t('app.title')}`;
  }, [t, location]);

  return (
    <PageLayout 
      title={t('stats.title')}
      subtitle={t('stats.subtitle')}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10 border border-gray-200 dark:border-gray-600">
        {!playerStats ? (
          <div className="text-center">
            {/* Upload Area */}
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 transition-colors duration-200 ${
                dragActive
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileInput}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={loading}
              />
              
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28l-8-8-4 4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                </svg>
                <div className="mt-4">
                  <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {t('stats.dragDropText')}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t('stats.supportedFormats')}
                  </p>
                </div>
                <div className="mt-6">
                  <button
                    type="button"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    disabled={loading}
                    onClick={() => {
                      fileInputRef.current?.click();
                      // Track file upload initiation
                      if (typeof gtag !== 'undefined') {
                        gtag('event', 'file_upload_initiated', {
                          'page': 'stats',
                          'upload_method': 'button_click'
                        });
                      }
                    }}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {t('stats.selectImage')}
                  </button>
                </div>
              </div>
            </div>

            {/* Sample Screenshot Button */}
            <div className="mt-8 text-center">
              <button
                onClick={() => setShowSampleModal(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {t('stats.viewSampleScreenshot')}
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="mt-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-300">{t('stats.processing')}</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="mt-8">
                <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded">
                  <strong className="font-bold">{t('stats.error')}</strong>
                  <span className="block sm:inline"> {error}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Player Stats Display */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                  {playerStats.username || 'Player Stats'}
                </h2>
                <button
                  onClick={resetStats}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {t('stats.uploadNew')}
                </button>
              </div>
            </div>

            {/* Auto-added to Leaderboard Status */}
            {addedToLeaderboard && (
              <div className="mb-6 text-center">
                <div className="inline-flex items-center px-4 py-2 rounded-md bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {isUpdate ? t('stats.updatedInLeaderboard') : t('stats.addedToLeaderboard')}
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-blue-200 dark:border-gray-500 max-w-md mx-auto">
                <h3 className="text-sm font-medium text-blue-600 dark:text-blue-300 mb-2">
                  {t('stats.inventoryPower')}
                </h3>
                <p className="text-3xl font-bold text-blue-800 dark:text-blue-200">
                  {playerStats.inventory_power?.toLocaleString() || 'N/A'}
                </p>
              </div>
            </div>

            {/* Gaming Time */}
            {playerStats.combat_performance_stats && playerStats.combat_performance_stats.total_battles && (
              <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-orange-200 dark:border-gray-500 mb-6">
                <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200 mb-4">
                  {t('stats.gamingTime')}
                </h3>
                <div className="text-center">
                  <div className="text-3xl font-bold text-orange-800 dark:text-orange-200 mb-2">
                    ~{calculateGamingTime(playerStats.combat_performance_stats.total_battles)?.formatted || '0m'}
                  </div>
                  <div className="text-sm text-orange-600 dark:text-orange-300">
                    {t('stats.gamingTimeDescription', { 
                      battles: playerStats.combat_performance_stats.total_battles?.toLocaleString() || '0',
                      minutes: calculateGamingTime(playerStats.combat_performance_stats.total_battles)?.totalMinutes || 0
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Combat Performance */}
            {playerStats.combat_performance_stats && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl border border-purple-200 dark:border-gray-500 mb-6">
                <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200 mb-4">
                  {t('stats.combatStats')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-sm text-purple-600 dark:text-purple-300 mb-1">
                      {t('stats.totalWins')}
                    </div>
                    <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
                      {playerStats.combat_performance_stats.total_wins?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-purple-600 dark:text-purple-300 mb-1">
                      {t('stats.totalBattles')}
                    </div>
                    <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
                      {playerStats.combat_performance_stats.total_battles?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-purple-600 dark:text-purple-300 mb-1">
                      {t('stats.totalKills')}
                    </div>
                    <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
                      {playerStats.combat_performance_stats.total_kills?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-purple-600 dark:text-purple-300 mb-1">
                      {t('stats.highestDamage')}
                    </div>
                    <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
                      {playerStats.combat_performance_stats.highest_damage?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-purple-600 dark:text-purple-300 mb-1">
                      {t('stats.winPercentage')}
                    </div>
                    <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
                      {playerStats.combat_performance_stats.win_percentage?.toFixed(1) || 'N/A'}%
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-purple-600 dark:text-purple-300 mb-1">
                      {t('stats.killsPerBattle')}
                    </div>
                    <div className="text-xl font-bold text-purple-800 dark:text-purple-200">
                      {playerStats.combat_performance_stats.kills_per_battle?.toFixed(2) || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}            
          </div>
        )}

        {/* Leaderboard Section */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              {t('stats.leaderboard')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {t('stats.leaderboardSubtitle')}
            </p>
            
            {/* Battle Requirement Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-2 text-blue-800 dark:text-blue-200">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">{t('stats.battleRequirement')}:</span>
                <span>{t('stats.battleRequirementDescription')}</span>
              </div>
            </div>
          </div>

          {/* Search and Controls */}
          {leaderboard && leaderboard.players && leaderboard.players.length > 0 && (
            <div className="mb-6 flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={t('stats.searchPlayersAndId')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                {t('stats.showingResults', { 
                  start: (currentPage - 1) * itemsPerPage + 1, 
                  end: Math.min(currentPage * itemsPerPage, getFilteredPlayers().length),
                  total: getFilteredPlayers().length 
                })}
              </div>
            </div>
          )}

          {loadingLeaderboard ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300">{t('stats.loadingLeaderboard')}</p>
            </div>
          ) : leaderboardError ? (
            <div className="text-center py-8">
              <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 px-4 py-3 rounded">
                <strong className="font-bold">{t('stats.errorLeaderboard')}</strong>
                <span className="block sm:inline"> {leaderboardError}</span>
              </div>
            </div>
          ) : leaderboard && leaderboard.players && leaderboard.players.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600" style={{ minWidth: '1200px' }}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-16"
                        onClick={() => handleSort('rank')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.rank')}</span>
                          {getSortIcon('rank')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-32"
                        onClick={() => handleSort('username')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.player')}</span>
                          {getSortIcon('username')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-24"
                        onClick={() => handleSort('player_id')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.playerId')}</span>
                          {getSortIcon('player_id')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-28"
                        onClick={() => handleSort('composite_score')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.compositeScore')}</span>
                          {getSortIcon('composite_score')}
                          <div className="group relative">
                            <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                            <div className="fixed bottom-auto left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[9999] top-32">
                              {t('stats.compositeScoreDescription')}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                            </div>
                          </div>
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-20"
                        onClick={() => handleSort('win_percentage')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.winPercentage')}</span>
                          {getSortIcon('win_percentage')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-20"
                        onClick={() => handleSort('total_wins')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.totalWins')}</span>
                          {getSortIcon('total_wins')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-20"
                        onClick={() => handleSort('total_battles')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.totalBattles')}</span>
                          {getSortIcon('total_battles')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-24"
                        onClick={() => handleSort('kills_per_battle')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.killsPerBattle')}</span>
                          {getSortIcon('kills_per_battle')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-28"
                        onClick={() => handleSort('inventory_power')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.inventoryPower')}</span>
                          {getSortIcon('inventory_power')}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none w-24"
                        onClick={() => handleSort('updated_at')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{t('stats.lastUpdated')}</span>
                          {getSortIcon('updated_at')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                    {getPaginatedPlayers().map((player, index) => (
                      <tr key={player.player_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                          #{player.rank}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <div className="truncate max-w-24" title={player.username}>
                            {player.username}
                          </div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                            {player.player_id}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {player.composite_score?.toFixed(1) || 'N/A'}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            {player.win_percentage.toFixed(1)}%
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {player.total_wins.toLocaleString()}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {player.total_battles.toLocaleString()}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            {player.kills_per_battle?.toFixed(2) || 'N/A'}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {player.inventory_power.toLocaleString()}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatRelativeTime(player.updated_at)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 text-sm text-gray-500 dark:text-gray-400">
                {t('stats.lastUpdated')}: {leaderboard.lastUpdated || 'Never'}
              </div>

              {/* Pagination Controls */}
              {getTotalPages() > 1 && (
                <div className="mt-6 flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('stats.previous')}
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === getTotalPages()}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('stats.next')}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {t('stats.showingPage', { 
                        current: currentPage, 
                        total: getTotalPages() 
                      })}
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">{t('stats.previous')}</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {/* Page Numbers */}
                      {Array.from({ length: Math.min(5, getTotalPages()) }, (_, i) => {
                        const startPage = Math.max(1, currentPage - 2);
                        const pageNum = startPage + i;
                        if (pageNum > getTotalPages()) return null;
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === currentPage
                                ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-200'
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === getTotalPages()}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">{t('stats.next')}</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500 dark:text-gray-400">
                <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>{t('stats.noPlayers')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sample Screenshot Modal */}
      {showSampleModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowSampleModal(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 mb-4">
                      {t('stats.sampleScreenshot')}
                    </h3>
                    <div className="text-center">
                      <img 
                        src="/sample-profile-screenshot.jpeg" 
                        alt="Sample Mech Arena Profile Screenshot"
                        className="max-w-full h-auto rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 mx-auto"
                      />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                        {t('stats.sampleDescription')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowSampleModal(false)}
                >
                  {t('stats.closeModal')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default CheckMyStats;
